import { useCallback, useRef, useState } from "react"
import { getCsrfToken } from "@/lib/csrf"
import {
  replaceContactProp,
  type ContactPropKey,
} from "@/lib/contact-prop-update"

// Inline-edit a single contact field from the v2 full-page
// contact show (Paper D20-0), the mobile contact show
// panel, or the unified slideover. Mirrors `useDealUpdate`:
// do an optimistic update on whichever contact prop the
// host page uses (`contact` for the show pages,
// `drawer.active.data` for the slideover), PATCH the field,
// revert on failure, surface validation errors back to the
// caller.
//
// Field-agnostic — callers pass the partial patch they
// want applied optimistically plus the raw payload sent
// to the server. Keeping them separate lets the UI render
// a formatted display string (e.g. "Atlanta, GA · UTC−5")
// while the server gets just the raw `city` value.

export interface ContactUpdatePayload {
  name?: string
  email?: string
  phone?: string
  job_title?: string
  company_name?: string
  linkedin_url?: string
  website?: string
  city?: string
  time_zone?: string
}

interface UseContactUpdateOptions {
  contactId: number
  contactPropKey: ContactPropKey
  onSuccess?: () => void
  onError?: (errors: Record<string, string>) => void
}

interface SubmitArgs {
  optimisticPatch: Record<string, unknown>
  payload: ContactUpdatePayload
}

interface UseContactUpdateResult {
  submitting: boolean
  submit: (args: SubmitArgs) => void
}

export function useContactUpdate({
  contactId,
  contactPropKey,
  onSuccess,
  onError,
}: UseContactUpdateOptions): UseContactUpdateResult {
  const [submitting, setSubmitting] = useState(false)
  // Synchronous in-flight guard — matches the deal-update
  // hook so a same-tick second tap collapses to one PATCH.
  const inFlight = useRef(false)

  const submit = useCallback(
    ({ optimisticPatch, payload }: SubmitArgs) => {
      if (inFlight.current) return
      inFlight.current = true

      let previous: Record<string, unknown> | null = null
      replaceContactProp(contactPropKey, (prev) => {
        previous = prev
        return { ...prev, ...optimisticPatch }
      })

      setSubmitting(true)
      fetch(`/v2/contacts/${contactId}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          v2_contacts_update_form: payload,
        }),
      })
        .then(async (response) => {
          if (response.status === 204) {
            onSuccess?.()
            return
          }

          if (response.status === 422) {
            const body = await safeJson(response)
            const errors =
              (body?.errors as Record<string, string>) ?? {}
            if (previous) {
              const snapshot = previous
              replaceContactProp(contactPropKey, () => snapshot)
            }
            onError?.(errors)
            return
          }

          throw new Error(`HTTP ${response.status}`)
        })
        .catch(() => {
          if (previous) {
            const snapshot = previous
            replaceContactProp(contactPropKey, () => snapshot)
          }
          onError?.({
            base: "Something went wrong. Please try again.",
          })
        })
        .finally(() => {
          inFlight.current = false
          setSubmitting(false)
        })
    },
    [contactId, contactPropKey, onError, onSuccess],
  )

  return { submitting, submit }
}

async function safeJson(response: Response) {
  try {
    return await response.json()
  } catch {
    return null
  }
}
